import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';

interface KPIRow {
  calls_current: number;
  calls_previous: number;
  meaningful_pct_current: number | null;
  meaningful_pct_previous: number | null;
  meaningful_calls_current: number;
  meaningful_calls_previous: number;
  new_contacts_current: number;
  new_contacts_previous: number;
  new_connections_current: number;
  new_connections_previous: number;
}

export interface KPIDataPoint {
  current: number;
  previous: number;
  deltaPct: number | null;
}

export interface KPIPctDataPoint {
  current: number | null;
  previous: number | null;
  deltaPct: number | null;
}

export interface KPIData {
  calls: KPIDataPoint;
  meaningfulPct: KPIPctDataPoint;
  meaningfulCalls: KPIDataPoint;
  newContacts: KPIDataPoint;
  newConnections: KPIDataPoint;
}

interface UseAnalyticsKPIsArgs {
  workspaceId: string;
  from: Date;
  to: Date;
  compareFrom: Date | null;
  compareTo: Date | null;
  userFilter: string | null;
  tz: string;
  enabled?: boolean;
}

function deltaPct(current: number, previous: number | null): number | null {
  if (previous === null) return null;
  if (previous === 0 && current === 0) return 0;
  if (previous === 0) return null; // signals "New" in the UI
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

export function useAnalyticsKPIs(opts: UseAnalyticsKPIsArgs): {
  data: KPIData | null;
  isLoading: boolean;
  error: Error | null;
} {
  const fromIso = opts.from.toISOString();
  const toIso = opts.to.toISOString();
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.analytics.kpis(opts.workspaceId, fromIso, toIso, opts.userFilter),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_dashboard_kpis', {
        p_workspace_id: opts.workspaceId,
        p_from: fromIso,
        p_to: toIso,
        p_compare_from: opts.compareFrom?.toISOString() ?? null,
        p_compare_to: opts.compareTo?.toISOString() ?? null,
        p_user_filter: opts.userFilter,
        p_tz: opts.tz,
      });
      if (error) throw error;
      const rows = (data ?? []) as KPIRow[];
      return rows[0] ?? null;
    },
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
  });

  if (!data) {
    return { data: null, isLoading, error: (error as Error) ?? null };
  }

  const result: KPIData = {
    calls: {
      current: data.calls_current,
      previous: data.calls_previous,
      deltaPct: deltaPct(data.calls_current, data.calls_previous),
    },
    meaningfulPct: {
      current: data.meaningful_pct_current,
      previous: data.meaningful_pct_previous,
      deltaPct:
        data.meaningful_pct_current != null && data.meaningful_pct_previous != null
          ? deltaPct(data.meaningful_pct_current, data.meaningful_pct_previous)
          : null,
    },
    meaningfulCalls: {
      current: data.meaningful_calls_current,
      previous: data.meaningful_calls_previous,
      deltaPct: deltaPct(data.meaningful_calls_current, data.meaningful_calls_previous),
    },
    newContacts: {
      current: data.new_contacts_current,
      previous: data.new_contacts_previous,
      deltaPct: deltaPct(data.new_contacts_current, data.new_contacts_previous),
    },
    newConnections: {
      current: data.new_connections_current,
      previous: data.new_connections_previous,
      deltaPct: deltaPct(data.new_connections_current, data.new_connections_previous),
    },
  };

  return { data: result, isLoading, error: (error as Error) ?? null };
}
